import React, { useContext } from 'react';
import { Button, Space, Typography, theme } from 'antd';
import { SaveOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { ThemeContext } from '../../index';

const { Text } = Typography;

/**
 * FloatingSaveBar - Reusable floating save bar component
 *
 * @param {function} onSave - Callback function called when save button is clicked
 * @param {boolean} saving - Loading state for save button
 * @param {boolean} disabled - Disable save button
 * @param {boolean} hasChanges - Whether form has unsaved changes (default: true)
 * @param {string} saveText - Text for save button (default: 'Save')
 * @param {ReactNode} extraButtons - Additional buttons to display
 * @param {string} layout - Layout style: 'left' (button on left) or 'space-between' (default)
 * @param {boolean} showWarning - Show warning message (default: true)
 * @param {number} leftOffset - Left offset to account for sidebar (default: 0)
 */
const FloatingSaveBar = ({
  onSave,
  saving = false,
  disabled = false,
  hasChanges = true,
  saveText = 'Save',
  extraButtons = null,
  layout = 'space-between',
  showWarning = true,
  leftOffset = 0
}) => {
  const { themeConfig } = useContext(ThemeContext);
  const isDarkMode = themeConfig?.algorithm === theme.darkAlgorithm;

  const justifyContent = layout === 'left' ? 'left' : 'space-between';
  const gap = layout === 'left' ? 16 : 0;

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: leftOffset,
      right: 0,
      padding: '16px 24px',
      backgroundColor: isDarkMode ? 'rgba(20, 20, 20, 0.95)' : 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      borderTop: `1px solid ${isDarkMode ? '#303030' : '#e8e8e8'}`,
      display: 'flex',
      justifyContent,
      alignItems: 'center',
      gap,
      zIndex: 1000
    }}>
      {layout === 'left' ? (
        <>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={onSave}
            loading={saving}
            disabled={disabled || !hasChanges}
            size="large"
            style={{ minWidth: 120 }}
          >
            {saveText}
          </Button>
          {showWarning && (
            <Space>
              <ExclamationCircleOutlined style={{ color: '#faad14' }} />
              <Text type="secondary">
                Please remember to save the page before leaving
              </Text>
            </Space>
          )}
          {extraButtons}
        </>
      ) : (
        <>
          {showWarning && (
            <Space>
              <ExclamationCircleOutlined style={{ color: '#faad14' }} />
              <Text type="secondary">
                Please remember to save before leaving
              </Text>
            </Space>
          )}
          <Space>
            {extraButtons}
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={onSave}
              loading={saving}
              disabled={disabled || !hasChanges}
              size="large"
              style={{ minWidth: 120 }}
            >
              {saveText}
            </Button>
          </Space>
        </>
      )}
    </div>
  );
};

export default FloatingSaveBar;
